import { FC, useState, useEffect } from 'react'
import { useSessionHistory } from '../hooks/useSessionHistory'
import { useQueueActions } from '../hooks/useQueueActions'
import { SellerServiceSessionItem } from '../types'
import Swal from 'sweetalert2'
import ReturnToQueueModal, { ReturnToQueueData } from './ReturnToQueueModal'
import {i18n} from '../../../../translate/i18n'

interface InServiceTabProps {
  pluginspaceId: number
  currentUserId: number
  isPrivileged: boolean
  onQueueChanged?: () => void
}

export const InServiceTab: FC<InServiceTabProps> = ({
  pluginspaceId,
  currentUserId,
  isPrivileged,
  onQueueChanged,
}) => {
  const { sessions, loading, loadHistory } = useSessionHistory(pluginspaceId)
  const { closeSellerAttendance, returnSellerToQueue, loading: actionLoading } =
    useQueueActions(pluginspaceId)
  const [inProgressSessions, setInProgressSessions] = useState<SellerServiceSessionItem[]>([])
  const [returnToQueueSession, setReturnToQueueSession] = useState<SellerServiceSessionItem | null>(null)
  const [finishAttendanceSession, setFinishAttendanceSession] = useState<SellerServiceSessionItem | null>(null)

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  useEffect(() => {
    const inProgress = sessions.filter((s) => s.status === 'in_progress')
    setInProgressSessions(inProgress)
  }, [sessions])

  const handleFinishSession = (session: SellerServiceSessionItem) => {
    setFinishAttendanceSession(session)
  }

  const handleFinishSessionConfirm = async (data: ReturnToQueueData) => {
    if (!finishAttendanceSession) return

    const success = await closeSellerAttendance(
      finishAttendanceSession.id,
      finishAttendanceSession.sellerId,
      data.saleOutcome === 'completed' ? 'sale_completed' : 'no_sale',
      data.noSaleReasonId
    )

    setFinishAttendanceSession(null)

    if (success) {
      Swal.fire('Sucesso', 'Atendimento encerrado', 'success')
      loadHistory()
      onQueueChanged?.()
    } else {
      Swal.fire('Erro', 'Erro ao encerrar atendimento', 'error')
    }
  }

  const handleReturnToQueue = (session: SellerServiceSessionItem) => {
    setReturnToQueueSession(session)
  }

  const handleReturnToQueueConfirm = async (data: ReturnToQueueData) => {
    if (!returnToQueueSession) return

    const success = await returnSellerToQueue(
      returnToQueueSession.id,
      returnToQueueSession.sellerId,
      data.password,
      data.saleOutcome === 'completed' ? 'sale_completed' : 'no_sale',
      data.noSaleReasonId
    )

    setReturnToQueueSession(null)

    if (success) {
      Swal.fire('Sucesso', 'Vendedor retornado para o final da fila', 'success')
      loadHistory()
      onQueueChanged?.()
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Sem permissão',
        text: 'Não foi possível retornar o vendedor. Verifique se a senha está correta e se você tem permissão para esta ação.',
      })
    }
  }

  const visibleSessions = isPrivileged
    ? inProgressSessions
    : inProgressSessions.filter((session) => session.sellerId === currentUserId)

  const translateStatus = (status: string) => {
    const normalized = status.toLowerCase()
    const key = `seller_queue.status.${normalized}`
    const translated = String(i18n.t(key))

    if (translated !== key) {
      return translated
    }

    switch (normalized) {
      case 'in_progress':
        return 'Em atendimento'
      case 'finished':
        return 'Finalizado'
      case 'waiting':
        return 'Aguardando'
      default:
        return status
    }
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}m ${secs}s`
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '300px' }}>
        <div className='spinner-border' role='status'>
          <span className='sr-only'>Carregando...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-header'>
        <h3 className='card-title'>Em Atendimento ({visibleSessions.length})</h3>
      </div>
      <div className='card-body'>
        {visibleSessions.length === 0 ? (
          <div className='text-center py-10'>
            <p className='text-muted'>Nenhum atendimento em progresso</p>
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 gy-7'>
              <thead>
                <tr className='fw-bold fs-6 text-gray-800'>
                  <th>Vendedor</th>
                  <th>Início</th>
                  <th>Duração</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {visibleSessions.map((session) => (
                  <tr key={session.id}>
                    <td>
                      <span className='fw-bold'>{session.sellerName || 'Vendedor ' + session.sellerId}</span>
                    </td>
                    <td>
                      <small>
                        {new Date(session.startedAt).toLocaleTimeString('pt-BR')}
                      </small>
                    </td>
                    <td>
                      {(() => {
                        const duration = Math.round(
                          (new Date().getTime() - new Date(session.startedAt).getTime()) / 1000
                        )
                        return formatDuration(duration)
                      })()}
                    </td>
                    <td>
                      <span className='badge badge-light-info'>{translateStatus(session.status)}</span>
                    </td>
                    <td>
                      <div className='d-flex gap-2'>
                        <button
                          className='btn btn-sm btn-danger'
                          onClick={() => handleFinishSession(session)}
                          disabled={actionLoading}
                          title='Encerra a sessão e remove o vendedor da fila'
                        >
                          <i className='fas fa-sign-out-alt'></i>
                        </button>
                        <button
                          className='btn btn-sm btn-primary'
                          onClick={() => handleReturnToQueue(session)}
                          disabled={actionLoading}
                          title='Retornar à fila'
                        >
                          <i className='fas fa-undo-alt'></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ReturnToQueueModal
        isOpen={!!returnToQueueSession}
        sellerName={returnToQueueSession?.sellerName || `Vendedor ${returnToQueueSession?.sellerId}`}
        onConfirm={handleReturnToQueueConfirm}
        onCancel={() => setReturnToQueueSession(null)}
      />

      <ReturnToQueueModal
        isOpen={!!finishAttendanceSession}
        title='Encerrar Atendimento'
        introText='Vendedor:'
        passwordIntroText='Para confirmar o encerramento do atendimento, informe a senha do vendedor, do gerente ou do administrador:'
        confirmText='Confirmar Encerramento'
        sellerName={finishAttendanceSession?.sellerName || `Vendedor ${finishAttendanceSession?.sellerId}`}
        onConfirm={handleFinishSessionConfirm}
        onCancel={() => setFinishAttendanceSession(null)}
      />
    </div>
  )
}
